import { useRef, useEffect } from 'react';

const ParticlesBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { willReadFrequently: false }); // Оптимизация для GPU
    if (!ctx) return;

    // Константы для анимации
    const FPS = 30;
    const FRAME_TIME = 1000 / FPS;

    // Оптимизированная настройка размера canvas с учетом DPI
    const setCanvasSize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = document.documentElement.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
    };

    setCanvasSize();
    
    // Оптимизация обработчика resize
    let resizeTimeout: NodeJS.Timeout;
    window.addEventListener('resize', () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(setCanvasSize, 200);
    });

    // Адаптивное количество частиц
    const getParticleCount = () => {
      const width = window.innerWidth;
      let count = width < 768 ? 400 : width < 1024 ? 600 : 800;
      
      if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4) {
        count = Math.floor(count * 0.8);
      }
      
      return count;
    };

    // Создание частиц с предварительным выделением памяти
    const particles = new Array(getParticleCount());
    for (let i = 0; i < particles.length; i++) {
      particles[i] = {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height * 1.5,
        speed: 0.5 + Math.random() * 0.3,
        size: 1.5 + Math.random() * 2,
        opacity: 0.15 + Math.random() * 0.15
      };
    }

    let animationFrameId: number;
    let lastTime = performance.now();

    const animate = (currentTime: number) => {
      if (!ctx || !canvas) return;

      const deltaTime = currentTime - lastTime;
      if (deltaTime < FRAME_TIME) {
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      lastTime = currentTime;

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Группируем операции рисования
      ctx.beginPath();
      const len = particles.length;
      for (let i = 0; i < len; i++) {
        const particle = particles[i];
        particle.y -= (particle.speed * deltaTime) / 16;

        if (particle.y < -10) {
          particle.y = canvas.height + 10;
          particle.x = Math.random() * canvas.width;
        }

        ctx.moveTo(particle.x, particle.y);
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      }

      ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
      ctx.fill();

      animationFrameId = requestAnimationFrame(animate);
    };

    animate(performance.now());

    return () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      window.removeEventListener('resize', setCanvasSize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1
      }}
    />
  );
};

export default ParticlesBackground; 